import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { RequestCancelAxios } from 'shared/api/lib'
import { Contact } from 'entities/Contacts/model/Contact'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { Conversation, ConversationsApi, type IResponseConversation } from 'entities/Conversation'
import { type IResponseContact } from 'entities/Contacts/api/types'
import { ContactsApi } from 'entities/Contacts'

export class ProfileLayoutPageStore {
  private _loading = true
  private _contactId: number | null = null
  private _messageId: number | null = null
  private _conversationId: number | null = null

  private _contactIdReady = false
  private _messageIdReady = false
  private _conversationIdReady = false
  private _limit = 0

  private _contact: Contact | null = null
  private _conversation: Conversation | null = null
  private _cancelRequest = new RequestCancelAxios()
  private _cancelLimitRequest = new RequestCancelAxios()

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  private _loadLimit = async () => {
    if (!this._contactId) return

    try {
      this._cancelLimitRequest.init()

      const response = await ConversationsApi.getConversationsSearch(
        {
          teams: [],
          contacts: [this._contactId],
          per_page: 1,
          page: 1,
        },
        {
          cancelToken: this._cancelLimitRequest.token,
        }
      )

      runInAction(() => {
        this._limit = Math.min(response.data.total, 200)
      })
    } catch (e) {
      logger.error(e)
    }
  }

  load = async () => {
    try {
      let conversationResponse: IResponseConversation | null = null
      let contactResponse: IResponseContact | null = null

      await this._loadLimit()

      this._cancelRequest.init()

      if (this._conversationId) {
        const [response] = await Promise.all([
          ConversationsApi.getById(this._conversationId, {
            cancelToken: this._cancelRequest.token,
          }),
          await this._entities.inboxesStore.initPromise,
        ])

        conversationResponse = response.data || null
        contactResponse = conversationResponse.contact || null
      } else if (this._contactId) {
        const [response] = await Promise.all([
          ContactsApi.getContactById(this._contactId, {
            cancelToken: this._cancelRequest.token,
          }),
          await this._entities.inboxesStore.initPromise,
        ])

        contactResponse = response.data
      }

      if (conversationResponse) {
        const response = await ContactsApi.getContactById(conversationResponse.contact_id, {
          cancelToken: this._cancelRequest.token,
        })

        const contactResponse = response.data

        runInAction(() => {
          this._entities.conversationStore.addItem(conversationResponse)
          this._entities.contactsStore.addItem(contactResponse)
          this._entities.contactsStore.addItems(conversationResponse.participants)

          this._conversation = new Conversation(conversationResponse, this._entities)
          this._contact = new Contact(contactResponse)
        })

        return true
      }

      if (contactResponse) {
        runInAction(() => {
          this._entities.contactsStore.addItem(contactResponse)
          this._contact = new Contact(contactResponse)
        })

        return true
      }

      return false
    } catch (e) {
      logger.error(e)

      return false
    }
  }

  dispose = () => {
    this._cancelRequest.reset()
    this._cancelLimitRequest.reset()
    this._loading = false
    this._contactId = null
    this._messageId = null
    this._contact = null
  }

  setContactId = (value: number) => {
    this._contactId = value
    this._contactIdReady = true
  }

  setConversationId = (value: number | null) => {
    this._conversationId = value
    this._conversationIdReady = true
  }

  setMessageId = (value: number | null) => {
    this._messageId = value
    this._messageIdReady = true
  }

  setLoading = (value: boolean) => {
    this._loading = value
  }

  get readyIsLoad() {
    return this._contactIdReady && this._conversationIdReady && this._messageIdReady
  }

  get readyIsNext() {
    if (!this._conversation) return Boolean(this._contact)

    return Boolean(this._conversation && this._contact)
  }

  get loading() {
    return this._loading
  }

  get contact() {
    return this._contact
  }

  get conversation() {
    return this._conversation
  }

  get messageId() {
    return this._messageId
  }

  get limit() {
    return this._limit
  }
}
